import React from "react"
import { Helmet } from "react-helmet"

import Layout from "../components/Layout"
import Header from "../components/Header"
import { gradientLightPink } from "../ds/gradients.module.css"
import { Link } from "gatsby"

type FaqItem = {
  question: string
  answer: string
  link?: { to: string; label: string }
}

type FaqCategory = {
  category: string
  id: string
  items: FaqItem[]
}

const faqCategories: FaqCategory[] = [
  {
    category: "General",
    id: "general",
    items: [
      {
        question: "What is Premagic?",
        answer:
          "Premagic is an AI-powered event technology platform that transforms event attendees into passionate brand advocates. It combines intelligent photo distribution, personalized marketing posters, AI avatar studios, and smart networking to maximize event ROI and amplify organic reach.",
      },
      {
        question: "How does Premagic work?",
        answer:
          "Premagic integrates with your event workflow. Before the event, attendees create personalized marketing posters to promote your event on social media. During the event, photographers or organizers upload photos to the platform, and our AI uses facial recognition to distribute the right photos to the right attendees — even picking their best shots automatically. Attendees can also create AI avatars and connect with other participants through smart networking. After the event, organizers get detailed analytics on engagement and reach.",
      },
      {
        question: "What types of events does Premagic support?",
        answer:
          "Premagic works for all event formats — conferences, trade shows, corporate events, product launches, award ceremonies, summits, expos, and hybrid or virtual events. Whether you have 100 or 100,000 attendees, the platform scales to fit your needs.",
      },
      {
        question: "Is Premagic suitable for small events?",
        answer:
          "Yes. Premagic offers usage-based pricing that scales with your event size, so it works for intimate gatherings of 50 people as well as large-scale conferences with tens of thousands of attendees. You only pay for what you use.",
      },
      {
        question: "How is Premagic different from other event platforms?",
        answer:
          "Most event platforms focus on registration and ticketing. Premagic focuses on what happens after registration — turning every attendee into an active promoter. Our AI-powered photo distribution, personalized marketing posters, avatar studios, and smart networking tools are designed to maximize engagement, organic reach, and event ROI. Our customers average 4x more registrations through organic sharing alone.",
      },
      {
        question:
          "Does Premagic work for virtual events, in-person events, or both?",
        answer:
          "All formats — virtual, in-person, and hybrid. The core strategy is the same: personalized content that attendees want to share, which multiplies your organic reach regardless of the event format.",
      },
    ],
  },
  {
    category: "Event Marketing Posters",
    id: "event-marketing-posters",
    items: [
      {
        question: "What are event marketing posters?",
        answer:
          "An event marketing poster is a personalized, shareable graphic that attendees post on social media to promote your event. Each poster features the attendee's name, photo, and job title alongside your event branding, creating authentic social proof that drives registrations.",
      },
      {
        question: "How do event marketing posters increase attendance?",
        answer:
          "They turn attendees into promoters. When someone shares a personalized poster, their network sees authentic social proof from someone they trust. This peer-to-peer promotion is far more effective than traditional ads. Our customers average 4x more registrations from organic poster sharing.",
      },
      {
        question: "Can I customize posters to match my brand?",
        answer:
          "Yes. You have full control over fonts, colors, layouts, and visuals. Create the template once, then share a link or embed our widget on your registration page. Attendees personalize it themselves — manually or via LinkedIn auto-fill. It takes them seconds and requires zero work from your team. Enterprise customers can also request custom designs.",
      },
      {
        question:
          "Which social media platforms can attendees share their posters on?",
        answer:
          "Attendees can share on Instagram, LinkedIn, WhatsApp, Email, Facebook, X (Twitter), WeChat, KakaoTalk, Snapchat, Telegram, and VKontakte. All posters are mobile-optimized for every platform with one-tap sharing.",
      },
      {
        question: "How do I track which attendees are promoting my event?",
        answer:
          "Premagic provides a real-time leaderboard showing who is sharing, clicking, and converting. Each poster has a unique tracking link so you can measure individual impact and reward top promoters. Download detailed reports anytime to analyze campaign performance.",
      },
    ],
  },
  {
    category: "Photo Distribution",
    id: "photo-distribution",
    items: [
      {
        question: "How does AI photo distribution work?",
        answer:
          "Premagic uses advanced facial recognition technology to automatically identify attendees in event photos. Once photos are uploaded by the event photographer, our AI matches faces and delivers the right photos to the right people — no manual tagging or sorting required.",
      },
      {
        question: "How quickly are photos delivered to attendees?",
        answer:
          "Photos are delivered in near real-time. As the event photographer uploads images, Premagic's AI processes and distributes them to identified attendees within minutes. Attendees receive their personalized photo gallery via a link — no app download needed.",
      },
      {
        question: "Can attendees download and share their photos?",
        answer:
          "Yes. Attendees can view, download, and share their photos directly from their personalized gallery. The sharing experience is optimized for social media, encouraging attendees to post their event photos and organically promote your brand.",
      },
      {
        question: "How does facial recognition work at events?",
        answer:
          "Attendees opt in by registering their face during event check-in or through the event registration flow. Our AI then scans all event photos and matches faces with registered profiles. The technology is fast, accurate, and fully compliant with privacy regulations including GDPR.",
      },
    ],
  },
  {
    category: "Avatar Studio",
    id: "avatar-studio",
    items: [
      {
        question: "What are AI avatars?",
        answer:
          "AI avatars are creative, AI-generated profile images that attendees can create at your event. Using Premagic's Avatar Studio, attendees take a quick photo and our AI transforms it into a stylized avatar that matches your event theme or branding. It is a fun, engaging experience that attendees love to share on social media.",
      },
      {
        question: "How do attendees create their AI avatars?",
        answer:
          "Attendees visit the Avatar Studio booth or use a digital kiosk at your event. They take a selfie or have their photo taken, choose from themed avatar styles curated for your event, and receive their AI-generated avatar within seconds. They can then instantly share it on social media with your event hashtags and branding.",
      },
      {
        question: "Can the Avatar Studio be customized for my event theme?",
        answer:
          "Yes. Avatar styles can be customized to match your event branding, theme, or campaign. Whether you want professional headshot styles, creative illustrations, or themed transformations, the Avatar Studio can be tailored to create a unique attendee experience.",
      },
    ],
  },
  {
    category: "Event Networking",
    id: "event-networking",
    items: [
      {
        question: "How does smart matchmaking work?",
        answer:
          "Premagic's smart networking uses AI to analyze attendee profiles, interests, and goals to suggest the most relevant connections. Instead of leaving networking to chance, attendees get curated recommendations for people they should meet, maximizing the value of their event experience.",
      },
      {
        question: "Can attendees connect before the event starts?",
        answer:
          "Yes. Premagic's networking features can be activated before the event, allowing attendees to browse profiles, schedule meetings, and start conversations ahead of time. This pre-event engagement builds excitement and ensures attendees arrive ready to make meaningful connections.",
      },
      {
        question:
          "What networking features are available during and after the event?",
        answer:
          "During the event, attendees can scan badges, exchange contact details, and receive AI-powered connection suggestions. After the event, they can access their list of connections, continue conversations, and leverage the network they have built — extending the value of your event well beyond its dates.",
      },
    ],
  },
  {
    category: "Pricing & Getting Started",
    id: "pricing-and-getting-started",
    items: [
      {
        question: "How much does Premagic cost?",
        answer:
          "Premagic offers usage-based pricing tailored to your event size and the features you need. There is no one-size-fits-all price — you only pay for what you use. Visit our pricing page or request a demo to get a customized quote for your event.",
      },
      {
        question: "Is there a free trial or demo available?",
        answer:
          "Yes. You can request a personalized demo to see Premagic in action with your specific event use case. Our team will walk you through the platform, answer your questions, and help you understand how Premagic can maximize your event ROI.",
      },
      {
        question: "How do I get started with Premagic?",
        answer:
          "Getting started is simple. Request a demo on our website, and our team will guide you through setting up your event on the platform. From onboarding to go-live, we provide hands-on support to ensure your event is a success.",
      },
      {
        question: "What support does Premagic offer?",
        answer:
          "Premagic provides dedicated support including onboarding assistance, event setup guidance, and a responsive support team available via email and chat. Enterprise customers receive a dedicated account manager and priority support throughout their event lifecycle.",
      },
    ],
  },
  {
    category: "Integrations",
    id: "integrations",
    items: [
      {
        question: "How do I connect my event platform to Premagic?",
        answer:
          "From inside the Premagic dashboard, you connect your event platform in a few clicks. We have direct API integrations with Swapcard, Luma, Cvent, Gatsby, HubSpot, Eventify and Swoogo. Once connected, Premagic backfills your existing registrations and subscribes to live updates so new registrations, edits and cancellations flow through automatically.",
        link: {
          to: "/integrations/how-it-works/#data-integration",
          label: "See the full data integration walkthrough",
        },
      },
      {
        question: "What if my event platform isn't supported yet?",
        answer:
          "Two options. We add new platforms regularly and can usually scope a new direct integration quickly if there's a clean API on the other side, so contact us with your platform. In the meantime, CSV import and export works with any platform: upload your registration list, work in Premagic, and re-export attendee gallery and poster links back into your platform.",
        link: {
          to: "/integrations/how-it-works/#data-integration",
          label: "Read about CSV vs API integration",
        },
      },
      {
        question: "Can I import attendees via CSV?",
        answer:
          "Yes. Export your registration list from your event platform as a CSV and upload it inside Premagic. We guide you through mapping columns to Premagic fields. Re-imports de-duplicate by email so existing attendees aren't touched and only new rows get added. Once Premagic has matched photos and generated posters, you can download a CSV with each attendee's gallery link, advocacy poster link and face ID to drop back into your platform.",
        link: {
          to: "/integrations/how-it-works/#data-integration",
          label: "See the CSV import and export details",
        },
      },
    ],
  },
  {
    category: "Security & Privacy",
    id: "security-and-privacy",
    items: [
      {
        question: "Is attendee data secure on Premagic?",
        answer:
          "Yes. Premagic takes data security seriously. We use industry-standard encryption for data in transit and at rest. Attendee data is stored securely and never shared with third parties. Our platform is built with privacy-by-design principles to protect every user.",
      },
      {
        question: "Is Premagic GDPR compliant?",
        answer:
          "Yes. Premagic is fully GDPR and CCPA compliant. We collect only the data necessary for the event experience, obtain explicit consent for facial recognition and data processing, and provide attendees full control over their data including the right to deletion. For LinkedIn auto-fill on posters, we use LinkedIn's official API with explicit user permission and only access name, photo, and job title.",
      },
      {
        question: "How does Premagic handle facial recognition data?",
        answer:
          "Facial recognition is strictly opt-in. Attendees must explicitly consent before their face data is processed. All biometric data is encrypted, used solely for photo matching during the event, and can be deleted upon request. We comply with all applicable biometric data regulations.",
      },
    ],
  },
]

// Flatten all FAQ items for JSON-LD schema
const allFaqs = faqCategories.flatMap(cat => cat.items)

const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: allFaqs.map(faq => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
}

export default function FaqPage() {
  return (
    <Layout
      title="FAQ | Frequently Asked Questions About Premagic Event Platform"
      description="Find answers about Premagic's AI-powered event platform — photo distribution, marketing posters, avatar studio, networking, pricing, and more."
      breadcrumbs={[
        { name: "Home", url: "/" },
        { name: "FAQ", url: "/faq/" },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema)}
        </script>
      </Helmet>

      <div>
        <div className={gradientLightPink}>
          <Header />
          <div className="pm-container">
            <h1 className="py-20 font-bold">
              Frequently Asked Questions
            </h1>
            <p className="font-normal text-sm text-gray-500 mt-2 pb-20">
              Everything you need to know about the Premagic event platform
            </p>
          </div>
        </div>

        <div className="pm-container py-10">
          {/* Category navigation */}
          <nav className="mb-12 flex flex-wrap gap-3">
            {faqCategories.map(cat => (
              <Link
                key={cat.id}
                to={`#${cat.id}`}
                className="text-sm px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
              >
                {cat.category}
              </Link>
            ))}
          </nav>

          {/* FAQ categories and items */}
          {faqCategories.map(cat => (
            <section key={cat.id} id={cat.id} className="mb-16">
              <h2 className="text-2xl font-bold mb-8 text-gray-900">
                {cat.category}
              </h2>
              {cat.items.map(faq => (
                <div key={faq.question} className="mb-10">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  {faq.link && (
                    <Link
                      to={faq.link.to}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2b4bc3] hover:text-[#1F0F61] transition-colors"
                    >
                      {faq.link.label}
                      <span aria-hidden>→</span>
                    </Link>
                  )}
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </Layout>
  )
}
